import React from "react";
import { useLayoutEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Button, IconButton, Tooltip } from "@mui/material";
import {
  FaExternalLinkAlt,
  FaSpinner
} from "react-icons/fa";
import Video, {
  createLocalAudioTrack,
  createLocalVideoTrack,
  LocalAudioTrack,
  LocalDataTrack,
  LocalVideoTrack
} from "twilio-video";
import { useFullpageLoading } from "../hooks/useFullPageLoading";
import { showToast } from "../utils/showToast";
import { useLocalParticipant } from "../hooks/useLocalParticipant";
import { ConfigDevicesModal } from "../components/Modals/ConfigDevicesModal";
import EffectsModal from "../components/Modals/EffectsModal";
import { useRoom } from "../hooks/useRoom";
import { getAccessToken } from "../api/rooms/getAccessToken";
import { useInfo } from "../hooks/useInfo";
import { notifyAccess } from "../api/rooms/notifyAccess";
import useQuery from "../utils/useQuery";
import { patchRecord } from "../api/rooms/handleRecord";
import RecordConfirmationModal from "../components/Modals/RecordConfirmationModal";
import VideoOffIndicator from "../components/VideoOffIndicator/VideoOffIndicator";
import { ITelemedicineConfig } from "../common/interfaces/ITelemedicineConfig";
import logoComTextoRoxa from "../assets/images/logos/logo_com_texto_roxa.png";
import { ConfigDevicesModalWhitelabel } from "../components/Modals/ConfigDevicesModalWhitelabel";
import { TooltipIcon } from "../components/icons/TooltipIcon/TooltipIcon";
import MenuRight from "../components/Menus/MenuRight";
import { ButtonsContainer } from "../components/Buttons/ButtonsContainer";
import { addPatientToQueue } from "../api/rooms/addPatientToQueue";
import { isPatientStillInQueue } from "../api/rooms/isPatientStillInQueue";
import { deletePatientToQueue } from "../api/rooms/deletePatientToQueue";
import { getWhiteLabelInfo } from "../api/rooms/getWhiteLabelInfo";
import { getProfessionalOnline } from "../api/rooms/getHasProfessionalOnline";
import { WaitingListPreview } from "../components/WaitingPatientsList/WaitingListPreview";
import alertSound from "../assets/sounds/aviso_fila.mp3";

interface StatusDotProps {
  isActive: boolean;
}

interface ContainerProps {
  onToggleWaitingList: () => void;
}

export const CameraPreview: React.FC = () => {
  const [showConfigDevicesModal, setShowConfigDevicesModal] = React.useState<boolean>(false);
  const [showRecordModal, setShowRecordModal] = React.useState<boolean>(false);
  const [localAudioTrack, setLocalAudioTrack] = React.useState<LocalAudioTrack>();
  const [localVideoTrack, setLocalVideoTrack] = React.useState<LocalVideoTrack>();
  const [selectedCamera, setSelectedCamera] = React.useState<any>(); //alterado de MediaDeviceInfo para conseguir encaixar a alteração do portal que vai mandar o id pela url
  const [selectedCameraWhitelabel, setSelectedCameraWhitelabel] = React.useState<any>(); //alterado de MediaDeviceInfo para conseguir encaixar a alteração do portal que vai mandar o id pela url
  const [isFrontFacing, setIsFrontFacing] = React.useState<boolean>(true);
  const [selectedMic, setSelectedMic] = React.useState<MediaDeviceInfo>();
  const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [clickedJoin, setClickedJoin] = React.useState<boolean>(false);
  const [roomIsEmpty, setRoomIsEmpty] = React.useState(false);
  const [whiteLabelConsentStatement, setWhiteLabelConsentStatement] = useState<string>("");
  const [showJoinButton, setShowJoinButton] = React.useState<boolean>(true);
  const buttonRefJoin = React.useRef<HTMLButtonElement>(null);
  const query = useQuery();
  const isWhitelabel = query.get("whitelabel") === "true";
  const deviceIdFromUrl = query.get("deviceId");

  const videoPreview = React.useRef<HTMLVideoElement>(null);
  const isRecording = React.useRef<boolean>(false);
  const roomToken = React.useRef<string>("");
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  const { audioOff, videoOff, handleAudio, handleVideo, setShowEffectsModal } = useLocalParticipant();
  const { room, setRoom, localTracks, setLocalTracks, handleSetConnected } = useRoom();
  const { handleShowFullPageLoading } = useFullpageLoading();
  const { telemedicineData } = useInfo();

  const record = telemedicineData.telemedicineConfiguration.gravar;
  const patientWaitingRoom = telemedicineData.telemedicineConfiguration.sala_espera;


  const isAndroid = /Android/i.test(navigator.userAgent);

  React.useEffect(() => {
    async function deletePatientCache() {
      if (telemedicineData.permission === "patient" && telemedicineData.participantId) {
        await deletePatientToQueue(telemedicineData.id, String(telemedicineData.participantId));
      }
    }
    deletePatientCache();
  }, []);

  const audioIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const audioTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  //criado uma função modificada para não afetar outros clientes fora do whitelabel
  const listDevicesWhitelabel = async () => {
    handleShowFullPageLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      navigator.mediaDevices.enumerateDevices()
        .then((mediaDevices) => {
          setDevices(mediaDevices);
          if (deviceIdFromUrl) {
            const device = mediaDevices.find((item) => item.kind === "videoinput");
            if (device) {
              setSelectedCamera(device);
              setSelectedCameraWhitelabel(deviceIdFromUrl);
            }
          } else {
            const defaultDevice = mediaDevices.find((item) => item.kind === "videoinput");
            setSelectedCamera(defaultDevice);
          }
          setSelectedMic(mediaDevices.find((item) => item.kind === "audioinput"));
        })
        .catch((error) => {
          showToast("error", { text: "Erro ao buscar pelos dispositivos" });
        })
        .finally(() => {
          handleShowFullPageLoading(false);
        });
    } catch (error) {
      showToast("error", { text: "Habilite as permissões de câmera e/ou microfone no seu navegador.", timer: 30000 });
      handleShowFullPageLoading(false);
    }
  };

  useLayoutEffect(() => {
    const query = useQuery();
    const telemedicineToken = query.get("token");
    const queueId = query.get("queueId");

    getWhiteLabelInfo(
      telemedicineToken, queueId,
    ).then((response) => {
      if (response) {
        setWhiteLabelConsentStatement(response.data.consentStatement || "");
      }
    });
  }, []);

  const listDevices = async () => {
    handleShowFullPageLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      navigator.mediaDevices.enumerateDevices()
        .then((mediaDevices) => {
          setDevices(mediaDevices);
          const selectedDevice = localStorage.getItem("SELECTED_VIDEO_DEVICE");
          let device: MediaDeviceInfo | undefined;
          if (selectedDevice && selectedDevice !== "") {
            device = mediaDevices.find((item) => item.deviceId === selectedDevice);
            if (!device) {
              device = mediaDevices.find((item) => item.kind === "videoinput");
            }
          } else {
            device = mediaDevices.find((item) => item.kind === "videoinput");
          }
          setSelectedCamera(device);
          setSelectedMic(mediaDevices.find((item) => item.kind === "audioinput"));
        })
        .catch((error) => {
          showToast("error", { text: "Erro ao buscar pelos dispositivos" });
        })
        .finally(() => {
          handleShowFullPageLoading(false);
        });
    } catch (error) {
      showToast("error", { text: "Habilite as permissões de câmera e/ou microfone no seu navegador.", timer: 30000 });
      handleShowFullPageLoading(false);
    }
  };

  const mountOtimizationTelemedicine = () => {
    const width = Number(process.env.REACT_APP_VIDEO_WIDTH ?? 800);
    const height = Number(process.env.REACT_APP_VIDEO_HEIGHT ?? 600);
    const frameRate = Number(process.env.REACT_APP_VIDEO_FRAMERATE ?? 30);
    const maxVideoBitrate = Number(process.env.REACT_APP_VIDEO_BITRATE ?? 0);
    const maxAudioBitrate = Number(process.env.REACT_APP_AUDIO_BITRATE ?? 0);
    const band = Number(process.env.REACT_APP_BANDWIDTH)

    const objQuality = {
      networkQuality: {
        local: 1,
        remote: 1,
      },
      tracks: [localAudioTrack, localVideoTrack, new LocalDataTrack()],
    };

    if (maxVideoBitrate <= 0) return objQuality;

    const bandData = {
      bandwidthProfile: {
        video: {
          maxSubscriptionBitrate: band,
        }
      },
      maxVideoBitrate,
      maxAudioBitrate,
      video: { width, height, frameRate },
    }

    return Object.assign(objQuality, bandData);
  }

  const connectToRoom = async () => {
    const response = await getAccessToken();
    if (!response) throw new Error("Falha ao obter token");

    roomToken.current = response.data.token;

    if (!localAudioTrack || !localVideoTrack) {
      console.log("****** Tracks não definidas: *******");
      console.log("localAudioTrack:", localAudioTrack);
      console.log("localVideoTrack:", localVideoTrack);
      console.log("********************");
      showToast("error", { text: "Habilite as permissões de câmera e/ou microfone no seu navegador.", timer: 30000 });
      return;
    }

    let bandWidthData: any = mountOtimizationTelemedicine();
    if (isWhitelabel) {
      bandWidthData = {
        tracks: [localAudioTrack, localVideoTrack, new LocalDataTrack()],
      };
    }

    Video.connect(roomToken.current, bandWidthData)
      .then(async (room) => {
        if (videoOff) {
          const pub = Array.from(room.localParticipant.videoTracks.values()).find(
            (p) => p.track.name === "camera"
          );
          if (pub) {
            await pub.track.restart();
            pub.track.disable();
            pub.track.stop();
          }
        }
        setRoom(room);
        handleSetConnected(true);
      })
      .catch((err) => {
        if (err.code === 53105) {
          showToast("error", {
            text: "A sala que está tentando se conectar já atingiu o número máximo de participantes",
            timer: 20000,
            showConfirmButton: true,
            confirmButtonColor: "#d40000",
          });
        } else if (err.code === 53205) {
          console.warn("O participante já está conectado na sala");
        } else {
          showToast("error", { text: "Erro ao tentar conectar-se à sala" });

          localTracks.forEach((track) => {
            if (track.kind !== "data") {
              track.stop();
            }
          });
          if (record && isRecording.current) {
            patchRecord(
              useQuery().get("token"),
              { action: "stop" }
            );
          }
          handleSetConnected(false);
        }
      })
      .finally(() => {
        handleShowFullPageLoading(false);
      });
  };
  const handleRecord = () => {
    handleShowFullPageLoading(true);
    patchRecord(
      useQuery().get("token"),
      { action: "start" }
    ).then((res) => {
      if (res) {
        isRecording.current = true;
        connectToRoom();
      }
    })
      .catch(() => {
        showToast("error", { text: "Erro ao iniciar a gravação" });
        handleShowFullPageLoading(false);
      });
  };

  const onAudioButtonClick = async () => {
    if (!localAudioTrack) return

    handleAudio(localAudioTrack);
  };

  const onVideoButtonClick = async () => {
    if (!localVideoTrack) return

    handleVideo(localVideoTrack);
  }


  const onJoinButtonClick = async () => {
    setClickedJoin(true);
    setShowJoinButton(false);
    handleShowFullPageLoading(true);

    try {
      if (audioIntervalRef.current) clearInterval(audioIntervalRef.current);
      if (audioTimeoutRef.current) clearTimeout(audioTimeoutRef.current);

      const res = await getAccessToken();
      if (!res) throw new Error("Falha ao obter token");

      roomToken.current = res.data.token;

      if (telemedicineData.permission === "professional" && record) {
        setShowRecordModal(true);
        return;
      }

      const previewRoom = await Video.connect(roomToken.current, {
        tracks: [],
      });

      if (
        telemedicineData.permission === "patient" &&
        previewRoom.participants.size === 0
      ) {
        handleShowFullPageLoading(false);
        setRoomIsEmpty(true);

        notifyAccess(useQuery().get("token") || "");

        if (!isWhitelabel) {
          const added = await addPatientToQueue(
            telemedicineData.id,
            telemedicineData.participantId,
            telemedicineData.name
          );

          if (!added) {
            showToast("error", { text: "Erro ao entrar na fila de espera." });
            return;
          }

          if (intervalRef.current) clearInterval(intervalRef.current);

          let attempts = 0;
          const waitTimeInMinutes = 40;
          const maxAttempts = waitTimeInMinutes * 60;

          intervalRef.current = setInterval(async () => {
            try {
              const res = await isPatientStillInQueue(
                telemedicineData.id,
                telemedicineData.participantId
              );

              if (!res.exists) {
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                }
                connectToRoom();
              }

              attempts++;
              if (attempts >= maxAttempts) {
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                }
                showToast("error", {
                  text: "Tempo esgotado aguardando liberação do profissional.",
                });
              }
            } catch (e) {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
              }
              showToast("error", { text: "Erro ao verificar fila de espera." });
            }
          }, 2000);

          if (!patientWaitingRoom) {
            previewRoom.on("participantConnected", async () => {
              try {
                await deletePatientToQueue(
                  telemedicineData.id,
                  String(telemedicineData.participantId)
                );
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                } 
                connectToRoom();
              } catch (e) {
                console.error("Erro ao remover paciente da fila:", e);
              }
            });
          }
        } else {
          previewRoom.on("participantConnected", async () => {
            connectToRoom();
          });
        }

        return;
      }

      setRoomIsEmpty(false);
      connectToRoom();
    } catch (err) {
      console.error(err);
    } finally {
      handleShowFullPageLoading(false);
      setClickedJoin(false);
    }
  };

  const onMicrophoneChange = () => {
    if (!selectedMic) {
      return;
    }
    handleShowFullPageLoading(true);
    createLocalAudioTrack({ name: "microphone", deviceId: { exact: selectedMic.deviceId } })
      .then((track) => {
        console.log("****** Track de audio criada *******");
        console.log("track:", track);
        console.log("*************************************");
        track.enable();
        if (audioOff) {
          track.disable();
        }
        setLocalAudioTrack(track);
      })
      .catch(() => {
        setLocalAudioTrack(undefined);
        showToast("error", { text: "Erro ao iniciar o microfone" });
      })
      .finally(() => {
        handleShowFullPageLoading(false);
      });
  };

  const onCameraChange = () => {
    const env_width = Number(process.env.REACT_APP_VIDEO_WIDTH);
    const env_heigth = Number(process.env.REACT_APP_VIDEO_HEIGHT);
    if (!selectedCamera) {
      return;
    }
    handleShowFullPageLoading(true);
    if (localVideoTrack) {
      localVideoTrack
        .restart({ deviceId: { exact: selectedCamera.deviceId } })
        .then(() => {
          console.log("****** Track de vídeo definada *******");
          console.log("track:", localVideoTrack);
          console.log("*************************************");
          if (videoPreview.current) {
            localVideoTrack.attach(videoPreview.current);

            if (videoPreview.current.paused) {
              videoPreview.current.play();
            }
          }

          localStorage.setItem("SELECTED_VIDEO_DEVICE", selectedCamera.deviceId);
          setIsFrontFacing(localVideoTrack.mediaStreamTrack.getSettings().facingMode === "user");
          setLocalTracks([localVideoTrack]);
        })
        .catch(() => {
          setLocalVideoTrack(undefined);
          showToast("info", { text: "Por favor, aguarde o profissional!" });
        })
        .finally(() => {
          handleShowFullPageLoading(false);
        });
      return;
    }
    createLocalVideoTrack({
      name: "camera",
      deviceId: { exact: selectedCamera.deviceId },
      frameRate: 24,
      width: isNaN(env_width) ? 800 : env_width,
      height: isNaN(env_heigth) ? 600 : env_heigth,
    })
      .then((track) => {
        console.log("****** Track de vídeo criada *******");
        console.log("track:", track);
        console.log("*************************************");
        localStorage.setItem("SELECTED_VIDEO_DEVICE", selectedCamera.deviceId);
        setLocalVideoTrack(track);
        setIsFrontFacing(track.mediaStreamTrack.getSettings().facingMode === "user");
        if (videoPreview.current) {
          track.attach(videoPreview.current);
          if (videoPreview.current.paused && !videoOff) {
            videoPreview.current.play();
          }
        }
        setLocalTracks([track]);
      })
      .catch((e) => {
        console.log(e)
        setLocalVideoTrack(undefined);
        showToast("error", { text: "Erro ao iniciar a câmera" });
      })
      .finally(() => {
        handleShowFullPageLoading(false);
      });
  };

  const onCameraChangeWhitelabel = () => {
    const env_width = Number(process.env.REACT_APP_VIDEO_WIDTH);
    const env_heigth = Number(process.env.REACT_APP_VIDEO_HEIGHT);
    if (!selectedCameraWhitelabel) {
      return;
    }
    handleShowFullPageLoading(true);

    const publishTrack = async (track: LocalVideoTrack) => {
      if (room && room.localParticipant) {
        room.localParticipant.videoTracks.forEach((publication) => {
          if (publication.track !== track) {
            room.localParticipant.unpublishTrack(publication.track);
            publication.track.stop();
          }
        });

        try {
          await room.localParticipant.publishTrack(track);
        } catch (error) {
          console.error("Erro ao simular track de vídeo: ", error);
        }
      }
    }

    if (localVideoTrack) {
      localVideoTrack
        .restart({ deviceId: { exact: selectedCameraWhitelabel.deviceId } })
        .then(async () => {
          console.log("****** Track de vídeo definada *******");
          console.log("track:", localVideoTrack);
          console.log("*************************************");
          if (videoPreview.current) {
            localVideoTrack.attach(videoPreview.current);
            if (videoPreview.current.paused) {
              videoPreview.current.play();
            }
          }
          localStorage.setItem("SELECTED_VIDEO_DEVICE", selectedCameraWhitelabel.deviceId);
          setIsFrontFacing(localVideoTrack.mediaStreamTrack.getSettings().facingMode === "user");
          setLocalTracks([localVideoTrack]);

          await publishTrack(localVideoTrack);
        })
        .catch(() => {
          setLocalVideoTrack(undefined);
          showToast("error", { text: "Erro ao iniciar a câmera" });
        })
        .finally(() => {
          handleShowFullPageLoading(false);
        });
      return;
    }

    createLocalVideoTrack({
      name: "camera",
      deviceId: { exact: isAndroid ? selectedCamera.deviceId : selectedCameraWhitelabel.deviceId },
      frameRate: 24,
      width: isNaN(env_width) ? 800 : env_width,
      height: isNaN(env_heigth) ? 600 : env_heigth,
    })
      .then((track) => {
        console.log("****** Track de vídeo criada *******");
        console.log("track:", track);
        console.log("*************************************");
        localStorage.setItem("SELECTED_VIDEO_DEVICE", isAndroid ? selectedCamera.deviceId : selectedCameraWhitelabel.deviceId);
        setLocalVideoTrack(track);
        setIsFrontFacing(track.mediaStreamTrack.getSettings().facingMode === "user");
        if (videoPreview.current) {
          track.attach(videoPreview.current);
          if (videoPreview.current.paused && !videoOff) {
            videoPreview.current.play();
          }
        }
        setLocalTracks([track]);
      })
      .catch((e) => {
        console.log(e)
        setLocalVideoTrack(undefined);
        showToast("error", { text: "Erro ao iniciar a câmera" });
      })
      .finally(() => {
        handleShowFullPageLoading(false);
      });
  };

  React.useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  React.useEffect(() => {
    onMicrophoneChange();
    return () => {
      console.log("Unmount | stopping localAudioTrack");
      localAudioTrack?.stop();
    };
  }, [selectedMic]);

  React.useEffect(() => {
    if (isWhitelabel && deviceIdFromUrl) {
      onCameraChangeWhitelabel();
    } else {
      onCameraChange();
    }
    return () => {
      console.log("Unmount | stopping localVideoTrack");
      localVideoTrack?.detach();
    };
  }, [selectedCamera, selectedCameraWhitelabel]);

  React.useEffect(() => {
    if (isWhitelabel && deviceIdFromUrl) {
      navigator.mediaDevices.addEventListener("devicechange", listDevicesWhitelabel);
      listDevicesWhitelabel();
      return () => {
        navigator.mediaDevices.removeEventListener("devicechange", listDevicesWhitelabel);
      };
    } else {
      navigator.mediaDevices.addEventListener("devicechange", listDevices);
      listDevices();
      return () => {
        navigator.mediaDevices.removeEventListener("devicechange", listDevices);
      };
    }
  }, []);

  const telemedicine: ITelemedicineConfig =
    telemedicineData.telemedicineConfiguration;

  const isWebView = useQuery().get("webview") === "true";

  React.useEffect(() => {
    if (isWhitelabel) {
      const callHandleChangeCamera = async () => {
        await handleChangeCamera();
      };

      callHandleChangeCamera();
    }
  }, []);

  const handleChangeCamera = async () => {
    const env_width = Number(process.env.REACT_APP_VIDEO_WIDTH);
    const env_heigth = Number(process.env.REACT_APP_VIDEO_HEIGHT);
    if (!selectedCameraWhitelabel) {
      return;
    }
    try {
      handleShowFullPageLoading(true);
      if (localVideoTrack) {
        await localVideoTrack.restart({ deviceId: { exact: selectedCameraWhitelabel.deviceId } });
        if (videoPreview.current) {
          localVideoTrack.attach(videoPreview.current);
          if (videoPreview.current.paused) {
            videoPreview.current.play();
          }
        }
        localStorage.setItem("SELECTED_VIDEO_DEVICE", selectedCameraWhitelabel.deviceId);
        setIsFrontFacing(localVideoTrack.mediaStreamTrack.getSettings().facingMode === "user");
        setLocalTracks([localVideoTrack]);
      } else {
        const track = await createLocalVideoTrack({
          name: "camera",
          deviceId: { exact: selectedCameraWhitelabel.deviceId },
          frameRate: 24,
          width: isNaN(env_width) ? 800 : env_width,
          height: isNaN(env_heigth) ? 600 : env_heigth,
        });
        localStorage.setItem("SELECTED_VIDEO_DEVICE", selectedCameraWhitelabel.deviceId);
        setLocalVideoTrack(track);
        setIsFrontFacing(track.mediaStreamTrack.getSettings().facingMode === "user");
        if (videoPreview.current) {
          track.attach(videoPreview.current);
          if (videoPreview.current.paused && !videoOff) {
            videoPreview.current.play();
          }
        }
        setLocalTracks([track]);
      }
    } catch (error) {
      setLocalVideoTrack(undefined);
      showToast("error", { text: "Erro ao iniciar a câmera" });
    } finally {
      handleShowFullPageLoading(false);
    }
  };
  React.useEffect(() => {
    if (isWhitelabel) {
      const query = useQuery();
      const automaticJoinRoomTele = query.get("automaticJoinRoomTele");
      if (automaticJoinRoomTele) {
        setTimeout(() => {
          buttonRefJoin.current?.click();
        }, +automaticJoinRoomTele)
      }
    }
  }, [])

  React.useEffect(() => {

    if (!isWhitelabel) return;

    const audio = new Audio(alertSound);
    const playAudio = () => {
      audio.currentTime = 0;
      audio.play().catch(err => {
        console.warn("Falha ao reproduzir áudio: ", err);
      });
    };

    const clearAudioTimers = () => {
      if (audioIntervalRef.current) clearInterval(audioIntervalRef.current);
      if (audioTimeoutRef.current) clearTimeout(audioTimeoutRef.current);
    };

    const removeUnlockListeners = () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
      window.removeEventListener("mousemove", unlockAudio);
    };

    const getTimeoutValue = () => {
      const value = query.get("automaticJoinRoomTele");
      const parsed = value ? parseInt(value, 10) : NaN;
      return !isNaN(parsed) ? parsed : 20000;
    };

    const unlockAudio = () => {
      playAudio();
      audioIntervalRef.current = setInterval(playAudio, 4000);
      audioTimeoutRef.current = setTimeout(clearAudioTimers, getTimeoutValue());
      removeUnlockListeners();
    };

    window.addEventListener("click", unlockAudio);
    window.addEventListener("touchstart", unlockAudio);
    window.addEventListener("mousemove", unlockAudio);

    return () => {
      clearAudioTimers();
      removeUnlockListeners();
    };
  }, []);

  const termoConsentimento = whiteLabelConsentStatement || telemedicine.termo_profissional || telemedicine.termo_geral;
  return (
    <Container isWhitelabel={isWhitelabel}>
      <Wrapper isWhitelabel={isWhitelabel}>
        {!isWhitelabel && (
          <>
            <LogoImg src={logoComTextoRoxa} alt="Logo" />
            <TopSideStatus>
              <CameraPreviewText>Prévia da câmera</CameraPreviewText>
              <MicrophoneStatusContainer>
                <StatusDot isActive={!!localAudioTrack} />
                {`Seu microfone ${localAudioTrack ? 'está' : 'não está'} funcionando corretamente`}
              </MicrophoneStatusContainer>
            </TopSideStatus>
          </>
        )}

        {isWhitelabel && (
          <ButtonsContainer
            isWhitelabel={isWhitelabel}
            showJoinButton={showJoinButton}
            showConfigDevicesButton={false}
            onJoinButtonClick={onJoinButtonClick}
            buttonRefJoin={buttonRefJoin}
          />
        )}

        <ContainerMessage>
          <VideoPreview
            ref={videoPreview}
            isWhitelabel={isWhitelabel}
            muted
            style={{ transform: isFrontFacing ? 'scaleX(-1)' : 'none' }}
            autoPlay
            disablePictureInPicture
          />
          {clickedJoin ? (
            <FaSpinner />
          ) : null}
          {roomIsEmpty ? (
            isWhitelabel ? (
              <MessageOverlayWhiteLabel>
                <MarginMobile>
                  <TooltipIcon />
                </MarginMobile>
                <MarginMobile>
                  A entrada na sala é liberada após o profissional conceder acesso à sala de atendimento.
                </MarginMobile>
              </MessageOverlayWhiteLabel>
            ) : (
              <MessageOverlay>
                <MarginMobile>
                  <TooltipIcon />
                </MarginMobile>
                <MarginMobile>
                  A entrada na sala é liberada após o profissional conceder acesso à sala de atendimento.
                </MarginMobile>
              </MessageOverlay>
            )
          ) : videoOff ? (
            isWhitelabel ? (
              <VideoPreviewContainerWhitelabel>
                <VideoOffIndicator />
              </VideoPreviewContainerWhitelabel>
            ) : (
              <VideoPreviewContainer>
                <VideoOffIndicator />
              </VideoPreviewContainer>
            )
          ) : null}
        </ContainerMessage>

        <ButtonsContainer
          isWhitelabel={isWhitelabel}
          showJoinButton={false}
          showConfigDevicesButton={true}
          videoOff={videoOff}
          audioOff={audioOff}
          roomIsEmpty={roomIsEmpty}
          onVideoButtonClick={onVideoButtonClick}
          onAudioButtonClick={onAudioButtonClick}
          setShowEffectsModal={setShowEffectsModal}
          setShowConfigDevicesModal={setShowConfigDevicesModal}
        />

        <EffectsModal />

        {isWhitelabel ? (
          <ConfigDevicesModalWhitelabel
            open={showConfigDevicesModal}
            devices={devices}
            onClose={() => setShowConfigDevicesModal(false)}
            selectedMic={selectedMic}
            selectedCamera={selectedCamera}
            changeSelectedMic={setSelectedMic}
            changeSelectedCamera={setSelectedCamera}
            changeSelectedCameraWhitelabel={setSelectedCameraWhitelabel}
          />
        ) : (
          <ConfigDevicesModal
            open={showConfigDevicesModal}
            devices={devices}
            onClose={() => setShowConfigDevicesModal(false)}
            selectedMic={selectedMic}
            selectedCamera={selectedCamera}
            changeSelectedMic={setSelectedMic}
            changeSelectedCamera={setSelectedCamera}
          />
        )}

        <RecordConfirmationModal
          opened={showRecordModal}
          handleClose={() => setShowRecordModal(false)}
          confirmAction={handleRecord}
        />

        {isWhitelabel && telemedicineData.permission === 'patient' && (
          <Termo>
            <Subtitle style={{ color: '#f9f9f9' }}>
              Ao clicar em entrar na sala você aceita o
            </Subtitle>
            <TermoConsentimento
              href={
                termoConsentimento ||
                'https://amplimedimggerais.s3-sa-east-1.amazonaws.com/TERMO+DE+CONSENTIMENTO+LIVRE+E+ESCLARECIDO.pdf'
              }
              target="_blank"
            >
              Termo de consentimento
              <FaExternalLinkAlt size={12} />
            </TermoConsentimento>
          </Termo>
        )}
      </Wrapper>
      {!isWhitelabel && (
        <MenuRight
          onJoinButtonClick={onJoinButtonClick}
          roomIsEmpty={roomIsEmpty}
          telemedicineData={telemedicineData}
          termoConsentimento={termoConsentimento}
        />
      )}

      {telemedicineData.permission === "professional" && telemedicine.sala_espera && !isWhitelabel && (
        <WaitingListPreviewWrapper>
          <WaitingListPreview />
        </WaitingListPreviewWrapper>
      )}
    </Container>
  );
};

const Wrapper = styled.div<{ isWhitelabel?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  width: 70%;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  ${({ isWhitelabel }) => isWhitelabel && `
    overflow: hidden;
    @container (max-width: 560px) {
      -webkit-overflow-scrolling: touch; 
      scrollbar-width: none; 
      &::-webkit-scrollbar {
        display: none;
      }
    }
  `}
  @container (max-width: 560px) {
    max-width: 100%;
    text-align: start;
    z-index: 2;
    height: auto;
    width: 90%;
  }
`;

const WaitingListPreviewWrapper = styled.div`
  display: block;

  @container (min-width: 561px) {
    display: none;
  }
`;

const LogoImg = styled.img`
  max-width: 225px;
  margin-top: 15px;
  margin-bottom: 0px;
  @container (min-width: 561px) {
    display: none;
  }
`;

const VideoPreviewContainer = styled.div`
  position: absolute;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center; 
  justify-content: center; 
  gap: 8px; 
  border-radius: 8px;
  width: 100%;
  height: 100%;
`;

const VideoPreview = styled.video<{ isWhitelabel?: boolean }>`
  display: ${({ hidden }) => (hidden ? "none" : "initial")};
  object-fit: cover;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  pointer-events: none;
  position: relative;
`;

const TopSideStatus = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 10px;
  @container (max-width: 760px) {
    display: none;
  }
`;

const CameraPreviewText = styled.div`
  color: white;
`;

const MicrophoneStatusContainer = styled.div`
  display: flex;
  align-items: center;
  color: white;
`;

const StatusDot = styled.div<StatusDotProps>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${(props) => (props.isActive ? 'green' : 'red')};
`;

const Subtitle = styled.p`
  color: #4c5667;
  font-size: 12px;
  font-weight: 300;
  margin: 0;
  padding-top: 10px;
`;

const Termo = styled.div`
  padding: 10px;
  text-align: center;
  z-index: 2;
`;


const Container = styled.div<{ isWhitelabel?: boolean }>`
  width: 100%;
  height: 100%;  
  display: flex;
  justify-content: space-between;
  align-items: center;
  @container (max-width: 560px) {
    flex-direction: column;
    justify-content: flex-start;
    overflow-y: scroll;
    background-color: ${({ isWhitelabel }) => (isWhitelabel ? 'transparent' : '#fff')};
  }
`;

const TermoConsentimento = styled("a")`
  color: ${(props) => props.theme.colors.primary};
  font-size: 12px;
  font-weight: 300;
  svg {
    margin-left: 5px;
  }
`;

const Header = styled("div")`
  max-width: 100%;
  display: flex;
  justify-content: center;

  @container (min-width: 665px) {
    margin-top: 0;
  }
`;

const VideoPreviewContainerWhitelabel = styled.div`
  position: absolute;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center; 
  justify-content: center; 
  gap: 8px; 
  width: 100%;
  height: 100%;
  border-radius: 8px;

  @container (max-width: 560px) {
    z-index: 2;
  }
`;

const MessageOverlay = styled.div`
  position: absolute;
  color: white;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center; 
  justify-content: center; 
  gap: 8px; 
  border-radius: 8px;
  width: 100%;
  height: 100%;

  @container (max-width: 560px) {
    flex-direction: column;
    gap: 6px;
    z-index: 2;
  }
`;

const MessageOverlayWhiteLabel = styled.div`
  position: absolute;
  color: white;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center; 
  justify-content: center; 
  gap: 8px; 
  border-radius: 8px;
  width: 100%;
  height: 100%;

  @container (max-width: 560px) {
    text-align: start;
    gap: 6px;
    z-index: 2;
  }
`;

const ContainerMessage = styled.div`
  position: relative;
  width: 100%;
  height: calc(70% - 70px);
  display: flex;
  margin-top: 14px;
  border-radius: 8px;
`;

const MarginMobile = styled.div`
  @container (max-width: 560px) {
    padding: 0 10px
  }
`;
